import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const Header = () => {
  const location = useLocation();
  const [sessionTime, setSessionTime] = useState(1740);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getSessionStatus = () => {
    if (sessionTime <= 300) return 'critical';
    if (sessionTime <= 600) return 'warning';
    return 'normal';
  };

  const navigationItems = [
    {
      label: 'Entry',
      path: '/tracking-number-entry-validation',
      icon: 'Package',
      tooltip: 'Track and validate shipment entries'
    },
    {
      label: 'History',
      path: '/shipment-history-search',
      icon: 'Search',
      tooltip: 'Search shipment history and records'
    },
    {
      label: 'Reports',
      path: '/reporting-analytics-dashboard',
      icon: 'BarChart3',
      tooltip: 'View analytics and reports'
    },
    {
      label: 'Team',
      path: '/team-management-user-administration',
      icon: 'Users',
      tooltip: 'Manage team members and permissions'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 bg-card shadow-elevation-2 z-100">
      <div className="h-[60px] flex items-center justify-between px-24">
        <div className="flex items-center gap-32">
          <Link to="/" className="flex items-center gap-12 no-underline">
            <div className="w-[48px] h-[48px] bg-primary/10 rounded-lg flex items-center justify-center transition-smooth hover:bg-primary/20">
              <Icon name="Package" size={28} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <span className="font-heading font-semibold text-h4 text-foreground">
              TrackingManager
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6" role="navigation" aria-label="Main navigation">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-12 px-24 h-[48px] rounded-lg
                  font-body font-medium text-[14px] no-underline
                  transition-smooth
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-elevation-1'
                    : 'text-foreground hover:bg-muted hover:text-foreground'
                  }
                `}
                title={item?.tooltip}
              >
                <Icon 
                  name={item?.icon} 
                  size={18} 
                  color={isActive(item?.path) ? 'var(--color-primary-foreground)' : 'currentColor'} 
                  strokeWidth={2} 
                />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-24">
          <div 
            className={`
              hidden md:flex items-center gap-12 px-18 h-[40px] rounded-lg
              font-caption font-medium
              ${getSessionStatus() === 'critical' ?'bg-error/10 text-error' 
                : getSessionStatus() === 'warning' ?'bg-warning/10 text-warning-foreground' :'bg-muted text-muted-foreground'
              }
            `}
            role="status"
            aria-live="polite"
            aria-label={`Session time remaining: ${formatTime(sessionTime)}`}
          >
            <Icon 
              name="Clock" 
              size={16} 
              color="currentColor" 
              strokeWidth={2} 
            />
            <span className="font-mono">{formatTime(sessionTime)}</span>
          </div>

          <div className="flex items-center gap-12 px-18 h-[40px] bg-muted rounded-lg">
            <div className="w-[32px] h-[32px] bg-primary rounded-full flex items-center justify-center">
              <span className="font-caption font-semibold text-primary-foreground text-[12px]">
                SC
              </span>
            </div>
            <div className="hidden lg:flex flex-col">
              <span className="font-caption font-medium text-foreground text-[13px] leading-tight">
                Sarah Chen
              </span>
              <span className="font-caption text-muted-foreground text-[11px] leading-tight">
                Coordinator
              </span>
            </div>
          </div>

          <button
            className="md:hidden w-[44px] h-[44px] flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
            aria-label="Open navigation menu"
            aria-expanded="false"
          >
            <Icon name="Menu" size={24} color="var(--color-foreground)" strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className="hidden md:hidden fixed inset-0 top-[60px] bg-card z-50 overflow-y-auto">
        <nav className="flex flex-col p-24 gap-12" role="navigation" aria-label="Mobile navigation">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`
                flex items-center gap-18 px-24 h-[56px] rounded-lg
                font-body font-medium text-[16px] no-underline
                transition-smooth
                ${isActive(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-elevation-1'
                  : 'text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon 
                name={item?.icon} 
                size={22} 
                color={isActive(item?.path) ? 'var(--color-primary-foreground)' : 'currentColor'} 
                strokeWidth={2} 
              />
              <div className="flex flex-col">
                <span>{item?.label}</span>
                <span className="font-caption text-[12px] opacity-80">
                  {item?.tooltip}
                </span>
              </div>
            </Link>
          ))}

          <div className="mt-24 pt-24 border-t border-border">
            <div className="flex items-center justify-between px-24 h-[56px] bg-muted rounded-lg">
              <div className="flex items-center gap-12">
                <Icon name="Clock" size={20} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="font-caption font-medium text-muted-foreground">
                  Session Time
                </span>
              </div>
              <span className={`
                font-mono font-medium text-[16px]
                ${getSessionStatus() === 'critical' ?'text-error' 
                  : getSessionStatus() === 'warning' ?'text-warning-foreground' :'text-foreground'
                }
              `}>
                {formatTime(sessionTime)}
              </span>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;